// Copyright (c) Docugami, Inc. All rights reserved.

import { SubscriptionType } from "networking/OrganizationApi";
import { useMemo } from "react";
import { daysLeftThreshold, maxPagesThreshold, selectIsActivePaidSubscription } from "store/AccountInfoHelpers";
import { useAppSelector } from "./redux";

interface IAccountView {
    // account info
    subscriptionType?: SubscriptionType;
    maxUsersCount?: number;
    expirationDate?: Date;
    pagesCount?: number;
    maxPages?: number;

    // account thresholds
    daysLeftThresholdReached: boolean;
    pageLimitThresholdReached: boolean;
}

export function useAccountView(): IAccountView {
    const organizationAccount = useAppSelector((state) => state.organizations.organizationAccount);
    const isActiveSubscriber = useAppSelector((state) => selectIsActivePaidSubscription(state));

    return useMemo((): IAccountView => {
        const subscription = organizationAccount?.subscription;
        const expirationDate = subscription?.expirationDate ? new Date(subscription.expirationDate) : undefined;

        // Cap the displayed page count to the max number of pages allowed
        const pageCount = subscription?.maxPagesQuota !== undefined ? Math.min(subscription.processedPageCount, subscription.maxPagesQuota) : subscription?.processedPageCount;

        return {
            subscriptionType: subscription?.subscriptionType,
            maxUsersCount: subscription?.maxUsers,
            expirationDate: expirationDate,
            pagesCount: pageCount,
            maxPages: subscription?.maxPagesQuota,

            daysLeftThresholdReached: !!expirationDate && daysLeftThreshold(expirationDate) && !isActiveSubscriber,
            pageLimitThresholdReached: subscription?.processedPageCount != null && subscription?.maxPagesQuota != null && maxPagesThreshold(subscription.processedPageCount, subscription.maxPagesQuota),
        };
    }, [organizationAccount, isActiveSubscriber])
}
